import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { OnlineAdmissionApi, OnlineAdmissionRow } from '@/services/online-admission.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select } from '@/components/ui/Input';

const GUARDIAN_GENDERS = ['male', 'female'] as const;
const STUDENT_GENDERS = ['male', 'female', 'other'] as const;

/** Best-effort split — matches the same convention as migrate-23-online-admission.ts's doc comment: first word = first name, rest = last name. Never persisted anywhere; purely a form pre-fill, always editable before submit. */
function splitName(full: string): { first: string; last: string } {
  const parts = full.trim().split(/\s+/);
  return { first: parts[0] ?? '', last: parts.slice(1).join(' ') };
}

export function AdmitOnlineAdmissionModal({ admission, onClose }: { admission: OnlineAdmissionRow | null; onClose: () => void }) {
  const qc = useQueryClient();
  const open = !!admission;

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState<(typeof STUDENT_GENDERS)[number]>('male');
  const [mobile, setMobile] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [academicYearId, setAcademicYearId] = useState('');
  const [admissionDate, setAdmissionDate] = useState(new Date().toISOString().slice(0, 10));
  const [guardianFirstName, setGuardianFirstName] = useState('');
  const [guardianLastName, setGuardianLastName] = useState('');
  const [guardianEmail, setGuardianEmail] = useState('');
  const [guardianMobile, setGuardianMobile] = useState('');
  const [guardianGender, setGuardianGender] = useState<(typeof GUARDIAN_GENDERS)[number]>('male');
  const [error, setError] = useState<string | null>(null);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'admit-form'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
    enabled: open,
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: currentYear } = useQuery({
    queryKey: ['academic-years', 'current', 'admit-form'],
    queryFn: () => AcademicYearsApi.current(),
    enabled: open,
  });

  useEffect(() => {
    if (!admission) return;
    const { first, last } = splitName(admission.name);
    setFirstName(first);
    setLastName(last);
    setDob(admission.dob ?? '');
    setGender((admission.gender as any) === 'female' ? 'female' : 'male');
    setMobile(admission.mobile ?? '');
    setClassId(admission.classId ?? '');
    setSectionId(admission.sectionId ?? '');
    const { first: gFirst, last: gLast } = splitName(admission.fatherName ?? '');
    setGuardianFirstName(gFirst);
    setGuardianLastName(gLast);
    setGuardianEmail('');
    setGuardianMobile(admission.mobile ?? '');
    setGuardianGender('male');
    setAdmissionDate(new Date().toISOString().slice(0, 10));
    setError(null);
  }, [admission]);

  useEffect(() => {
    if (currentYear && !academicYearId) setAcademicYearId(currentYear.id);
  }, [currentYear, academicYearId]);

  const admit = useMutation({
    mutationFn: () =>
      OnlineAdmissionApi.admit(admission!.id, {
        firstName,
        lastName: lastName || undefined,
        dob,
        gender,
        mobile: mobile || undefined,
        sectionId,
        academicYearId,
        admissionDate,
        guardian: {
          firstName: guardianFirstName,
          lastName: guardianLastName || undefined,
          email: guardianEmail.trim() || undefined,
          mobile: guardianMobile,
          gender: guardianGender,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['online-admissions'] });
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Could not admit this student.'),
  });

  const valid = firstName.trim() && dob && sectionId && academicYearId && admissionDate && guardianFirstName.trim() && guardianMobile.trim();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Admit & Edit"
      description="Review and complete the application before admitting — this creates the student record."
      size="xl"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={admit.isPending}>
            Cancel
          </button>
          <button className="btn-primary" onClick={() => admit.mutate()} disabled={!valid || admit.isPending}>
            {admit.isPending ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
            Admit
          </button>
        </>
      }
    >
      {!admission ? null : (
        <div className="space-y-5">
          {error && (
            <div className="flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                &times;
              </button>
            </div>
          )}

          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-700">Student</h4>
            <div className="grid grid-cols-2 gap-4">
              <Field label="First Name" required>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </Field>
              <Field label="Last Name">
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Field>
              <Field label="Date of Birth" required>
                <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
              </Field>
              <Field label="Gender" required>
                <Select value={gender} onChange={(e) => setGender(e.target.value as any)}>
                  {STUDENT_GENDERS.map((g) => (
                    <option key={g} value={g}>
                      {g[0].toUpperCase() + g.slice(1)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Mobile">
                <Input value={mobile} onChange={(e) => setMobile(e.target.value)} />
              </Field>
            </div>
          </div>

          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-700">Placement</h4>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Class" required>
                <Select
                  value={classId}
                  onChange={(e) => {
                    setClassId(e.target.value);
                    setSectionId('');
                  }}
                >
                  <option value="">Select</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Section" required>
                <Select value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
                  <option value="">Select</option>
                  {sections.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Admission Date" required>
                <Input type="date" value={admissionDate} onChange={(e) => setAdmissionDate(e.target.value)} />
              </Field>
            </div>
          </div>

          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-700">Guardian</h4>
            <p className="mb-2 text-xs text-slate-400">
              The application only captured a father's name and mobile — email wasn't collected on the public form. It's optional here; leave it blank if you don't have one.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Field label="First Name" required>
                <Input value={guardianFirstName} onChange={(e) => setGuardianFirstName(e.target.value)} />
              </Field>
              <Field label="Last Name">
                <Input value={guardianLastName} onChange={(e) => setGuardianLastName(e.target.value)} />
              </Field>
              <Field label="Email">
                <Input type="email" value={guardianEmail} onChange={(e) => setGuardianEmail(e.target.value)} />
              </Field>
              <Field label="Mobile" required>
                <Input value={guardianMobile} onChange={(e) => setGuardianMobile(e.target.value)} />
              </Field>
              <Field label="Gender" required>
                <Select value={guardianGender} onChange={(e) => setGuardianGender(e.target.value as any)}>
                  {GUARDIAN_GENDERS.map((g) => (
                    <option key={g} value={g}>
                      {g[0].toUpperCase() + g.slice(1)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
